import { Injectable } from '@angular/core';
import { catchError, from, Observable, of, switchMap, tap } from 'rxjs';
import { ApiConfig } from 'src/app/core/apiConfig';
import { BannersDataService } from 'src/app/core/data/admin/bannerData';
import { BaseResponse, bannerRequestDTO, bannerResponse } from 'src/app/core/models';
import { UtilityService } from '../../shared-services/utility.service';
import { environment } from 'src/environments/environment';

const CACHE_KEY = '/Landing/getbanners';
const MAX_AGE = 15 * 24 * 60 * 60 * 1000; // 15 days in milliseconds
const salesUrl = environment.salesUrl;

const CACHE_KEY_GET_ALL = '/ConfigureServices/get';
@Injectable({
  providedIn: 'root',
})
export class BannersService extends BannersDataService {
  enableConfigurationSection(data?: any): Observable<BaseResponse<any>> {
    return this.utilityService.postPut<bannerResponse[]>(ApiConfig.admin.enableConfigurationSection, data);
  }

  enableBannerSection(data: any): Observable<BaseResponse<any>> {
    return this.utilityService.postPut<any>(ApiConfig.admin.enableBannerSection, data);
  }

  getAll(): Observable<BaseResponse<any>> {
    // Check if caching is supported (to prevent errors on HTTP)
    if (!('caches' in window)) {
      return this.utilityService.postPut<any>(ApiConfig.admin.getAllProductAndServices, null);
    }

    return from(caches.open('api-cache')).pipe(
      switchMap((cache) =>
        from(cache.match(CACHE_KEY_GET_ALL)).pipe(
          switchMap(async (cachedResponse) => {
            if (cachedResponse) {
              const cachedData = await cachedResponse.json();
              const cachedTime = new Date(cachedData.timestamp).getTime();
              const now = Date.now();

              // Check if the cache entry is expired
              if (now - cachedTime < MAX_AGE) {
                return cachedData.response as BaseResponse<any>;
              } else {
                // If expired, delete the cached entry
                await cache.delete(CACHE_KEY_GET_ALL);
              }
            }
            // No cache or expired, call the API
            return null;
          }),
          switchMap((cachedData) => {
            // Make the API call and cache the response
            return this.utilityService.postPut<any>(ApiConfig.admin.getAllProductAndServices, null).pipe(
              tap((response) => {
                // Cache the new response with timestamp
                const responseWithTimestamp = {
                  response,
                  timestamp: new Date().toISOString(),
                };
                cache.put(CACHE_KEY_GET_ALL, new Response(JSON.stringify(responseWithTimestamp)));
              }),
            );
          }),
        ),
      ),
      catchError(() => {
        // In case of any error, fall back to the API call
        return this.utilityService.postPut<any>(ApiConfig.admin.getAllProductAndServices, null);
      }),
    );
  }

  save(data: any, id?: string): Observable<BaseResponse<any>> {
    return this.utilityService.postPut<bannerResponse[]>(ApiConfig.admin.saveProductAndServices, data, id);
  }
  deleteBanners(data: any): Observable<BaseResponse<any>> {
    return this.utilityService.postPut<bannerRequestDTO[]>(ApiConfig.admin.deleteBanners, data);
  }

  getHomePageBanners(id?: string): Observable<BaseResponse<bannerRequestDTO[]>> {
    return this.utilityService.postPut<bannerRequestDTO[]>(ApiConfig.admin.getBanners, null, id);
  }

  saveBanners(data: any, id?: string): Observable<BaseResponse<any>> {
    return this.utilityService.postPut<bannerResponse[]>(ApiConfig.admin.saveBanners, data, id);
  }

  getAllBanners(id?: string): Observable<BaseResponse<bannerRequestDTO[]>> {
    return this.utilityService.postPut<bannerRequestDTO[]>(ApiConfig.admin.landingPageManagement, null, id);
  }

  getAllModules(id?: string): Observable<BaseResponse<any[]>> {
    return this.utilityService.postPut<any[]>(ApiConfig.admin.getmodules, null, id);
  }
  getAllSalesBanners(id?: string): Observable<BaseResponse<bannerResponse[]>> {
    // Check if caching is supported (to prevent errors on HTTP)
    if (!('caches' in window)) {
      return this.utilityService.postPut<bannerResponse[]>(ApiConfig.salesUrl.getSalesBanners, null, id);
    }

    return from(caches.open('api-cache')).pipe(
      switchMap((cache) =>
        from(cache.match(CACHE_KEY)).pipe(
          switchMap(async (cachedResponse) => {
            if (cachedResponse) {
              const cachedData = await cachedResponse.json();
              const cachedTime = new Date(cachedData.timestamp).getTime();
              const now = Date.now();

              // Check if the cache entry is expired
              if (now - cachedTime < MAX_AGE) {
                return cachedData.response as BaseResponse<bannerResponse[]>;
              } else {
                // If expired, delete the cached entry
                await cache.delete(CACHE_KEY);
              }
            }
            // No cache or expired, call the API
            return null;
          }),
          switchMap((cachedData) => {
            // Make the API call and cache the response
            return this.utilityService.postPut<bannerResponse[]>(ApiConfig.salesUrl.getSalesBanners, null, id).pipe(
              tap((response) => {
                // Cache the new response with timestamp
                const responseWithTimestamp = {
                  response,
                  timestamp: new Date().toISOString(),
                };
                cache.put(CACHE_KEY, new Response(JSON.stringify(responseWithTimestamp)));
              }),
            );
          }),
        ),
      ),
      catchError(() => {
        // In case of any error, fall back to the API call
        return this.utilityService.postPut<bannerResponse[]>(ApiConfig.salesUrl.getSalesBanners, null, id);
      }),
    );
  }
  constructor(private utilityService: UtilityService) {
    super();
  }

  // Comment methods
  getCommentsByBannerId(bannerId: number): Observable<BaseResponse<any[]>> {
    return this.utilityService.get<any[]>(`${salesUrl}/Banners/comments/${bannerId}`, null, null, 'get');
  }

  saveComment(commentData: any): Observable<BaseResponse<any>> {
    const url = `${salesUrl}/Banners/savecomment`;
    return this.utilityService.postPut<any>(url, commentData, null, 'post');
  }

  likeComment(commentId: number): Observable<BaseResponse<any>> {
    return this.utilityService.postPut<any>(`${salesUrl}/Banners/comments/${commentId}/like`, {}, null, 'post');
  }

          unlikeComment(commentId: number): Observable<BaseResponse<any>> {
            return this.utilityService.postPut<any>(`${salesUrl}/Banners/comments/${commentId}/unlike`, {}, null, 'post');
          }

          // Banner social methods
          likeBanner(bannerId: number): Observable<BaseResponse<any>> {
            return this.utilityService.postPut<any>(`${salesUrl}/Banners/like-banner/${bannerId}`, {}, null, 'post');
          }
        }
